<template>
    <div v-if="helpPanelStore.isOpen" class="help-panel">
        <div class="help-panel-content">
            <!-- Close Button -->
            <button class="help-panel-close" @click="helpPanelStore.closePanel()">
                <img class="icon" src="/img/icons/avid-icons/close.svg" alt="Close">
            </button>

            <!-- Video Player Mockup -->
            <div class="help-panel-video">
                <div class="video-player-mockup">
                    <button v-if="!helpPanelStore.isPlaying" class="video-play-button" @click="handlePlayClick">
                        <span class="play-icon-text">▶</span>
                    </button>
                    <div v-else class="video-playing-overlay">
                        <!-- Video is playing -->
                    </div>
                </div>
                
                <!-- Video Controls -->
                <div class="video-controls">
                    <button class="control-btn" @click="jumpBack" title="Jump back 15s">
                        <img src="/img/icons/avid-icons/arrow-left.svg" alt="Jump back 15s" class="control-icon">
                        <span class="control-label">15s</span>
                    </button>
                    <button class="control-btn" @click="rewind" title="Rewind">
                        <img src="/img/icons/avid-icons/arrow-left.svg" alt="Rewind" class="control-icon">
                    </button>
                    <button class="control-btn" @click="handlePlayClick" :title="helpPanelStore.isPlaying ? 'Pause' : 'Play'">
                        <span v-if="!helpPanelStore.isPlaying" class="control-icon-text">▶</span>
                        <span v-else class="control-icon-text">⏸</span>
                    </button>
                    <button class="control-btn" @click="fastForward" title="Fast forward">
                        <img src="/img/icons/avid-icons/arrow-right.svg" alt="Fast forward" class="control-icon">
                    </button>
                    <button class="control-btn" @click="jumpForward" title="Jump forward 15s">
                        <img src="/img/icons/avid-icons/arrow-right.svg" alt="Jump forward 15s" class="control-icon">
                        <span class="control-label">15s</span>
                    </button>
                </div>
                
                <!-- Step Navigation Buttons -->
                <div class="step-navigation">
                    <button 
                        class="btn btn-secondary step-nav-btn" 
                        @click="goToPreviousStep"
                        :disabled="helpPanelStore.currentStepIndex === 0"
                    >
                        <img src="/img/icons/avid-icons/arrow-left.svg" alt="Previous" class="step-nav-icon">
                        Previous Step
                    </button>
                    <button 
                        class="btn btn-primary step-nav-btn" 
                        @click="goToNextStep"
                        :disabled="!hasNextStep"
                    >
                        Next Step
                        <img src="/img/icons/avid-icons/arrow-right.svg" alt="Next" class="step-nav-icon">
                    </button>
                </div>
            </div>

            <!-- Steps Section -->
            <div class="help-panel-steps">
                <div 
                    v-for="(step, index) in helpPanelStore.currentVideo?.steps || []" 
                    :key="index"
                    class="help-step"
                    :class="{ 'help-step-active': index === helpPanelStore.currentStepIndex }"
                    :data-step-index="index"
                >
                    <h4 class="help-step-title">{{ step.title }}</h4>
                    <a 
                        :href="getStepUrl(step.url)" 
                        class="help-step-link"
                        @click.prevent="navigateToStep(step.url, index)"
                    >
                        {{ getStepUrl(step.url) }}
                        <i class="fas fa-external-link-alt" style="font-size: 0.75rem;"></i>
                    </a>
                    <div class="help-step-content" v-html="formatStepContent(step.content)"></div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { watch, onMounted, computed } from 'vue';
import { useHelpPanelStore } from '../../stores/helpPanel';
import { useClientStore } from '../../stores/client';
import { router } from '@inertiajs/vue3';

const helpPanelStore = useHelpPanelStore();
const clientStore = useClientStore();

/**
 * Check if there's a next step available
 */
const hasNextStep = computed(() => {
    if (!helpPanelStore.currentVideo || !helpPanelStore.currentVideo.steps) {
        return false;
    }
    return helpPanelStore.currentStepIndex < helpPanelStore.currentVideo.steps.length - 1;
});

/**
 * Get the full URL for a step, replacing {client} placeholder
 */
function getStepUrl(url) {
    if (!url) return '#';
    const clientUrl = clientStore.client?.url || '';
    return url.replace('{client}', clientUrl);
}

/**
 * Format step content with paragraphs and bullet points
 */
function formatStepContent(content) {
    if (!content) return '';
    
    // Split by double newlines for paragraphs
    const paragraphs = content.split(/\n\n+/);
    let html = '';
    let inList = false;
    
    paragraphs.forEach(para => {
        const trimmed = para.trim();
        if (!trimmed) return;
        
        // Check if this paragraph contains bullet points
        const lines = trimmed.split('\n');
        const hasBullets = lines.some(line => /^\* /.test(line.trim()));
        
        if (hasBullets) {
            if (inList) {
                html += '</ul>';
            }
            html += '<ul>';
            inList = true;
            
            // Process each line
            lines.forEach(line => {
                const trimmedLine = line.trim();
                const bulletMatch = trimmedLine.match(/^\* (.+)$/);
                if (bulletMatch) {
                    html += `<li>${bulletMatch[1]}</li>`;
                } else if (trimmedLine && !trimmedLine.startsWith('*')) {
                    // Regular text before bullets - close list and add paragraph
                    if (inList) {
                        html += '</ul>';
                        inList = false;
                    }
                    html += `<p>${trimmedLine}</p>`;
                }
            });
        } else {
            if (inList) {
                html += '</ul>';
                inList = false;
            }
            html += `<p>${trimmed}</p>`;
        }
    });
    
    if (inList) {
        html += '</ul>';
    }
    
    return html;
}

/**
 * Navigate to a step URL and scroll to hash target
 */
function navigateToStep(url, stepIndex) {
    const fullUrl = getStepUrl(url);
    if (!fullUrl || fullUrl === '#') return;
    
    // Update step index
    helpPanelStore.setStep(stepIndex);
    
    // Parse URL and hash
    const urlObj = new URL(fullUrl, window.location.origin);
    const path = urlObj.pathname;
    const hash = urlObj.hash;
    
    // Navigate using Inertia
    router.visit(path + hash, {
        preserveState: true,
        preserveScroll: false,
        onSuccess: () => {
            // Scroll to hash target after navigation
            if (hash) {
                setTimeout(() => {
                    const target = document.querySelector(hash);
                    if (target) {
                        target.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    }
                }, 100);
            }
        }
    });
}

/**
 * Handle play button click - navigate to first step if starting
 */
function handlePlayClick() {
    if (!helpPanelStore.isPlaying && helpPanelStore.currentStepIndex === 0) {
        // If starting from the beginning, navigate to first step
        const firstStep = helpPanelStore.currentVideo?.steps?.[0];
        if (firstStep && firstStep.url) {
            navigateToStep(firstStep.url, 0);
        }
    }
    helpPanelStore.togglePlay();
}

/**
 * Video control handlers
 */
function jumpBack() {
    // Mock functionality
    console.log('Jump back 15 seconds');
}

function rewind() {
    // Mock functionality
    console.log('Rewind');
}

function fastForward() {
    // Mock functionality
    console.log('Fast forward');
}

function jumpForward() {
    // Mock functionality
    console.log('Jump forward 15 seconds');
}

/**
 * Navigate to previous step
 */
function goToPreviousStep() {
    if (helpPanelStore.currentStepIndex > 0) {
        const prevIndex = helpPanelStore.currentStepIndex - 1;
        const prevStep = helpPanelStore.currentVideo?.steps?.[prevIndex];
        if (prevStep && prevStep.url) {
            navigateToStep(prevStep.url, prevIndex);
            scrollToStep(prevIndex);
        }
    }
}

/**
 * Navigate to next step
 */
function goToNextStep() {
    if (hasNextStep.value) {
        const nextIndex = helpPanelStore.currentStepIndex + 1;
        const nextStep = helpPanelStore.currentVideo?.steps?.[nextIndex];
        if (nextStep && nextStep.url) {
            navigateToStep(nextStep.url, nextIndex);
            scrollToStep(nextIndex);
        }
    }
}

/**
 * Scroll to a specific step in the steps list
 */
function scrollToStep(stepIndex) {
    setTimeout(() => {
        const stepElement = document.querySelector(`.help-step[data-step-index="${stepIndex}"]`);
        if (stepElement) {
            stepElement.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }
    }, 100);
}

// Watch for step changes to auto-navigate (only when video is "playing")
watch(() => helpPanelStore.currentStepIndex, (newIndex, oldIndex) => {
    // Only auto-navigate if video is playing and step actually changed
    if (helpPanelStore.isPlaying && newIndex !== oldIndex && oldIndex !== undefined) {
        const step = helpPanelStore.currentStep;
        if (step && step.url) {
            // Auto-navigate when step changes (e.g., from video "playback")
            navigateToStep(step.url, newIndex);
        }
    }
});

onMounted(() => {
    // Scroll to active step when panel opens
    if (helpPanelStore.currentStepIndex >= 0) {
        setTimeout(() => {
            const activeStep = document.querySelector('.help-step-active');
            if (activeStep) {
                activeStep.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
            }
        }, 100);
    }
});
</script>

